import { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { CheckCircle, Loader2 } from 'lucide-react'
import { useApi } from '../hooks/useApi'
import { useData } from '../hooks/useData'
import LoadingSpinner from '../components/LoadingSpinner'
import Modal from '../components/Modal'

/**
 * Public survey landing page.
 *
 * Identity: the (projectToken, recipientToken) pair in the URL path. The
 * backend generates a durable opaque recipient token per stakeholder at send
 * time and persists it on the project doc, so the same recipient opening the
 * link twice always hits the same token. We never ask for the respondent's
 * email on this page - that would both annoy the user and give them a way to
 * fake a different identity.
 *
 * Dedup: one submission per recipient token, enforced server-side by hashing
 * the token and storing the hash alongside the response. The localStorage
 * flag is a second-layer convenience check that short-circuits the network
 * round-trip for repeat visits from the same device.
 */
type LookupResult = {
  projectId: string
  projectName: string
  contact: { name?: string; email?: string } | null
} | null

export default function Survey() {
  const api = useApi()
  const { projectToken, recipientToken } = useParams<{
    projectToken: string
    recipientToken: string
  }>()

  const { data: lookup, loading } = useData<LookupResult>(
    () => api.getSurveyLookup(projectToken!, recipientToken!),
    [projectToken, recipientToken],
  )

  // localStorage key is recipient-specific so different recipients on the
  // same browser don't share state.
  const storageKey = `survey_submitted_${projectToken}_${recipientToken}`
  // alreadySubmitted: null = server check pending, true = already submitted, false = not
  const [alreadySubmitted, setAlreadySubmitted] = useState<boolean | null>(
    localStorage.getItem(storageKey) ? true : null,
  )
  const [submitted, setSubmitted] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [validationModal, setValidationModal] = useState(false)
  const [formData, setFormData] = useState({
    techSatisfaction: 0,
    peopleSatisfaction: 0,
    additionalComments: '',
  })

  // Server-side dedup check once the lookup confirms the token pair is valid.
  useEffect(() => {
    if (!lookup || alreadySubmitted === true) return
    api
      .checkSurveySubmitted(projectToken!, recipientToken!)
      .then(res => setAlreadySubmitted(res.submitted))
      .catch(() => setAlreadySubmitted(false))
  }, [lookup, projectToken, recipientToken])

  // The main app CSS sets html/body/#root to overflow:hidden to prevent double scrollbars
  // on the dashboard layout. The survey page is a standalone public page with no sidebar,
  // so we need to override those rules to allow normal scrolling.
  useEffect(() => {
    const root = document.getElementById('root')
    const html = document.documentElement
    const prev = {
      htmlOverflow: html.style.overflow,
      htmlHeight: html.style.height,
      bodyOverflow: document.body.style.overflow,
      bodyHeight: document.body.style.height,
      rootOverflow: root?.style.overflow ?? '',
      rootHeight: root?.style.height ?? '',
    }
    html.style.overflow = 'auto'
    html.style.height = 'auto'
    document.body.style.overflow = 'auto'
    document.body.style.height = 'auto'
    if (root) {
      root.style.overflow = 'auto'
      root.style.height = 'auto'
    }
    return () => {
      html.style.overflow = prev.htmlOverflow
      html.style.height = prev.htmlHeight
      document.body.style.overflow = prev.bodyOverflow
      document.body.style.height = prev.bodyHeight
      if (root) {
        root.style.overflow = prev.rootOverflow
        root.style.height = prev.rootHeight
      }
    }
  }, [])

  const RatingButtons = ({
    value,
    onChange,
    lowLabel,
    highLabel,
  }: {
    value: number
    onChange: (v: number) => void
    lowLabel: string
    highLabel: string
  }) => (
    <div>
      <div className="flex gap-2">
        {[1, 2, 3, 4, 5].map(num => (
          <button
            key={num}
            type="button"
            onClick={() => onChange(num)}
            className={`flex-1 py-3 border-2 rounded-xl font-bold transition-all ${
              value === num
                ? 'border-[#00AEEF] bg-[#00AEEF]/10 text-[#00AEEF]'
                : 'border-slate-200 text-slate-600 hover:border-[#00AEEF] hover:text-[#00AEEF]'
            }`}
          >
            {num}
          </button>
        ))}
      </div>
      <div className="flex justify-between text-xs text-slate-400 mt-2 px-1">
        <span>{lowLabel}</span>
        <span>{highLabel}</span>
      </div>
    </div>
  )

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!lookup || formData.techSatisfaction === 0 || formData.peopleSatisfaction === 0) {
      setValidationModal(true)
      return
    }
    setSubmitting(true)

    try {
      const trimmedComments = formData.additionalComments.trim()
      await api.submitSurvey({
        projectToken: projectToken!,
        recipientToken: recipientToken!,
        techSatisfaction: formData.techSatisfaction,
        peopleSatisfaction: formData.peopleSatisfaction,
        additionalComments: trimmedComments || undefined,
      })
    } catch (err: any) {
      // 409 → dedup race (refresh after submit, concurrent tabs); show
      // the "Already Submitted" state instead of a scary error.
      if (err?.status === 409 || err?.data?.error === 'already_submitted') {
        setAlreadySubmitted(true)
        setSubmitting(false)
        return
      }
      throw err
    }

    localStorage.setItem(storageKey, '1')
    setSubmitted(true)
    setSubmitting(false)
  }

  if (loading || alreadySubmitted === null) return <LoadingSpinner fullScreen />

  if (!lookup)
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="card p-8 text-center max-w-md">
          <h1 className="text-xl font-bold text-slate-800 mb-2">Survey Not Found</h1>
          <p className="text-slate-500">This survey link is invalid or has expired.</p>
        </div>
      </div>
    )

  if (alreadySubmitted === true && !submitted)
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="card p-8 text-center max-w-md">
          <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-8 h-8 text-slate-400" />
          </div>
          <h1 className="text-xl font-bold text-slate-800 mb-2">Already Submitted</h1>
          <p className="text-slate-500">You've already submitted feedback for this project.</p>
        </div>
      </div>
    )

  if (submitted)
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="card p-8 text-center max-w-md">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
          <h1 className="text-xl font-bold text-slate-800 mb-2">Thank You!</h1>
          <p className="text-slate-500">Your feedback has been submitted successfully.</p>
        </div>
      </div>
    )

  return (
    <div className="min-h-screen bg-[#F0F9FF] flex items-center justify-center p-8">
      <Modal
        open={validationModal}
        title="Required Fields"
        message="Please fill in all required fields."
        onConfirm={() => setValidationModal(false)}
      />
      <div className="w-full max-w-lg">
        <div className="bg-white rounded-3xl shadow-xl overflow-hidden">
          {/* Header */}
          <div className="bg-[#005EB8] px-8 py-6">
            <img src="/InvertedLogo.png" alt="Danone" className="h-10 mb-4" />
            <h1 className="text-2xl font-bold text-white">Project Feedback</h1>
            <p className="text-white/80 text-sm mt-1">{lookup.projectName}</p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-8">
            <p className="text-slate-600 text-sm mb-6">
              This survey is anonymous. Your individual responses will not be attributed to you.
            </p>

            <div className="mb-6">
              <label className="block text-sm font-semibold text-slate-800 mb-3">
                How well does the solution meet your business needs?{' '}
                <span className="text-red-500">*</span>
              </label>
              <RatingButtons
                value={formData.techSatisfaction}
                onChange={v => setFormData({ ...formData, techSatisfaction: v })}
                lowLabel="Not fit for purpose"
                highLabel="Fully fit for purpose"
              />
            </div>

            <div className="mb-6">
              <label className="block text-sm font-semibold text-slate-800 mb-3">
                How would you rate the project team's professionalism and communication?{' '}
                <span className="text-red-500">*</span>
              </label>
              <RatingButtons
                value={formData.peopleSatisfaction}
                onChange={v => setFormData({ ...formData, peopleSatisfaction: v })}
                lowLabel="Very poor"
                highLabel="Excellent"
              />
            </div>

            <div className="mb-6">
              <label className="block text-xs font-medium text-slate-500 mb-2">
                Additional comments <span className="text-slate-400">(optional)</span>
              </label>
              <textarea
                value={formData.additionalComments}
                onChange={e => setFormData({ ...formData, additionalComments: e.target.value })}
                placeholder="Anything else you'd like to share?"
                rows={3}
                maxLength={1000}
                className="w-full px-3 py-2 text-sm rounded-xl border border-slate-200 bg-slate-50/50 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#005EB8]/20 focus:border-[#005EB8]/40 resize-none"
              />
            </div>

            {lookup.contact && (lookup.contact.name || lookup.contact.email) && (
              <div className="mb-6 px-4 py-3 bg-slate-50 rounded-xl border border-slate-200 text-xs text-slate-600">
                <div className="text-[11px] uppercase tracking-wide font-semibold text-slate-400 mb-0.5">
                  Questions? Contact
                </div>
                {lookup.contact.name && (
                  <div className="font-semibold text-slate-700">{lookup.contact.name}</div>
                )}
                {lookup.contact.email && (
                  <a
                    href={`mailto:${lookup.contact.email}`}
                    className="text-[#005EB8] hover:underline"
                  >
                    {lookup.contact.email}
                  </a>
                )}
              </div>
            )}

            <button
              type="submit"
              disabled={
                submitting ||
                formData.techSatisfaction === 0 ||
                formData.peopleSatisfaction === 0
              }
              className="w-full btn-primary text-white py-3.5 rounded-xl font-bold text-sm disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
              {submitting ? 'Submitting...' : 'Submit Feedback'}
            </button>
          </form>
        </div>
      </div>
    </div>
  )
}
